import {useCallback, useEffect, useMemo, useRef, useState, type ReactNode} from 'react';
import {Keyboard, Share2, Sparkles, X} from 'lucide-react';
import {useIntl} from 'react-intl';
import {DialogPrimitive as Dialog, PhoneHQSpinner, Segmented, SkeletonList, type SegmentedOption} from 'ch3-ui-lib';
import {
    invalidateCallInsights,
    useFetchCallQuestionsQuery,
    useFetchCallRecordQuery,
    useFetchCallSummaryQuery,
    useFetchCallTranscriptQuery,
    useRequestCallSummaryMutation,
    useRequestCallTranscriptionMutation,
    type Call,
    type Citation,
    type InsightTopic,
} from '../../../store/features/calls/callsService';
import {releaseStartCallPill, suppressStartCallPill} from '../../../features/callSurface/store/callSurfaceSlice';
import {useIsCallAskAiEnabledQuery} from '../../../services/chatService';
import {showToast} from '../../../store/features/Notifications/notificationsSlice';
import {useAppDispatch} from '../../../store/store';
import {formatTime} from '../../../utilis/timeUtilis';
import ContactPanel from '../../Contacts/ContactPanel';
import {AskAiView} from './ask/AskAiView';
import {CopyButton} from './CopyButton';
import {errorStatus, generationErrorMessage, isFinalError} from './insightsErrors';
import {
    logContactSuggestionOpened,
    logContentCopied,
    logDrawerClosed,
    logDrawerOpened,
    logGenerationFailed,
    logGenerationRequested,
    logGenerationSucceeded,
    logPlaybackSeek,
    logTabChanged,
    logTranscriptExported,
    type GenerationMode,
    type TabChangeVia,
} from './insightsLog';
import {insightsMessages as m} from './insightsMessages';
import {generationBlocker, hasRecordingAudio, isRecordingDeleted, isVoicemailCall, type InsightsBlocker} from './insightsModel';
import {iconButton, lucide, tintButton} from './insightsUi';
import {InsightsActionsContext, type InsightsActions} from './InsightsActions';
import {InsightsParticipants} from './InsightsParticipants';
import {InsightsPlayer} from './player/InsightsPlayer';
import {PlaybackContext} from './player/PlaybackContext';
import {RecordingDeletedNotice} from './player/RecordingDeletedNotice';
import {useAudioPlayback} from './player/useAudioPlayback';
import {ShortcutsHelp} from './ShortcutsHelp';
import type {ContactPanelRequest} from './summary/ContactSuggestionCard';
import {GenerationStatus} from './summary/GenerationStatus';
import {summaryToText} from './summary/summaryText';
import {SummaryView, type SummaryAvailability} from './summary/SummaryView';
import {useFollowUpDrafts} from './summary/useFollowUpDrafts';
import {SpeakerTimeline} from './timeline/SpeakerTimeline';
import {computeTalkStats} from './timeline/talkStats';
import {TalkStatsBar} from './timeline/TalkStatsBar';
import {findActiveIndex, findSegmentIndexById} from './transcript/activeSegment';
import {downloadText, transcriptFileName, transcriptToText} from './transcript/exportTranscript';
import {resolveSegments, speakerCast} from './transcript/speakers';
import {TranscriptView, type TranscriptTarget} from './transcript/TranscriptView';
import {useTranscriptSearch} from './transcript/useTranscriptSearch';
import {copyText} from './useCopyFeedback';
import {useDrawerShortcuts, type DrawerShortcutHandlers} from './useDrawerShortcuts';
import {insightsShareUrl, type InsightsEntry, type InsightsTab} from './useInsightsDrawerParams';

const TARGET_HIGHLIGHT_MS = 2000;

export interface CallInsightsDrawerProps {
    callId: number;
    call: Call | undefined;
    callLoading: boolean;
    callFailed: boolean;
    tab: InsightsTab;
    initialTime: number | null;
    entry: InsightsEntry;
    onTabChange: (tab: InsightsTab) => void;
    onClose: () => void;
    returnFocus?: HTMLElement | null;
}

const callDurationSeconds = (call: Call | undefined): number => {
    const start = call?.bridgetAt ?? call?.startedAt;
    if (!start || !call?.finishedAt) return 0;
    return Math.max(0, (new Date(call.finishedAt).getTime() - new Date(start).getTime()) / 1000);
};

const Centered = ({children}: {children: ReactNode}) => (
    <div className="flex flex-1 flex-col items-center justify-center gap-3 px-6 pb-24 text-center">{children}</div>
);

export const CallInsightsDrawer = ({callId, call, callLoading, callFailed, tab, initialTime, entry, onTabChange, onClose, returnFocus}: CallInsightsDrawerProps) => {
    const intl = useIntl();
    const dispatch = useAppDispatch();
    const hasCall = Boolean(call);

    const transcriptQuery = useFetchCallTranscriptQuery(callId, {skip: !hasCall});
    const summaryQuery = useFetchCallSummaryQuery(callId, {skip: !hasCall});
    const audioAvailable = hasRecordingAudio(call);
    const deleted = isRecordingDeleted(call);
    const {data: recordingUrl, isError: recordingFailed} = useFetchCallRecordQuery(Number(call?.recordingId), {skip: !audioAvailable});
    const [requestSummary, summaryRequest] = useRequestCallSummaryMutation();
    const [requestTranscript, transcriptRequest] = useRequestCallTranscriptionMutation();
    const askAiFeature = useIsCallAskAiEnabledQuery();
    const askEnabled = askAiFeature.data === true;

    const transcript = transcriptQuery.error ? undefined : transcriptQuery.data;
    const summary = summaryQuery.error ? undefined : summaryQuery.data;

    const cast = useMemo(() => speakerCast(call, {
        client: intl.formatMessage(m.clientFallback),
        agent: intl.formatMessage(m.agentFallback),
    }), [call, intl]);
    const segments = useMemo(() => resolveSegments(transcript?.transcript, cast), [transcript, cast]);
    const hasTranscript = segments.length > 0;
    const voicemail = isVoicemailCall(call);
    const stats = useMemo(
        () => computeTalkStats(segments.map((segment) => ({side: segment.speaker.side, startTime: segment.startTime, endTime: segment.endTime})), {voicemail}),
        [segments, voicemail],
    );
    const speechEnd = segments.reduce((end, segment) => Math.max(end, segment.endTime), 0);
    const fallbackDuration = Math.max(speechEnd, callDurationSeconds(call));

    const playback = useAudioPlayback(recordingUrl ?? null, {initialTime, fallbackDuration});
    const search = useTranscriptSearch(segments);
    const searchRef = useRef<HTMLInputElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);
    const [contactRequest, setContactRequest] = useState<ContactPanelRequest | null>(null);
    const [helpOpen, setHelpOpen] = useState(false);
    const [target, setTarget] = useState<TranscriptTarget | null>(null);
    const [searchFocusRequest, setSearchFocusRequest] = useState(0);
    const handledSearchFocus = useRef(0);
    const followUp = useFollowUpDrafts(callId);

    useEffect(() => {
        dispatch(suppressStartCallPill());
        return () => {
            dispatch(releaseStartCallPill());
        };
    }, [dispatch]);

    const questionsQuery = useFetchCallQuestionsQuery(callId, {skip: tab !== 'ask' || !hasTranscript || !askEnabled});

    useEffect(() => {
        if (tab === 'ask' && !askEnabled && !askAiFeature.isLoading) onTabChange('summary');
    }, [askAiFeature.isLoading, askEnabled, onTabChange, tab]);

    const selectTab = useCallback((next: InsightsTab, via: TabChangeVia) => {
        if (next === tab || (next === 'ask' && !askEnabled)) return;
        onTabChange(next);
        logTabChanged({tab: next, via});
    }, [askEnabled, onTabChange, tab]);

    const close = useCallback(() => {
        logDrawerClosed({callId, tab});
        onClose();
    }, [callId, onClose, tab]);

    const opened = useRef(false);
    const settled = !callLoading && (!hasCall || (!transcriptQuery.isLoading && !summaryQuery.isLoading));
    useEffect(() => {
        if (opened.current || !settled) return;
        opened.current = true;
        logDrawerOpened({callId, entry, tab, hasTranscript, insightsVersion: summary?.insightsVersion ?? null});
    }, [settled, callId, entry, tab, hasTranscript, summary]);

    const sharedTarget = useRef(false);
    useEffect(() => {
        if (sharedTarget.current || !hasTranscript || initialTime == null) return;
        sharedTarget.current = true;
        const index = findActiveIndex(segments, initialTime);
        if (index >= 0) setTarget({index, nonce: Date.now()});
    }, [hasTranscript, initialTime, segments]);

    useEffect(() => {
        if (!target) return;
        const timer = setTimeout(() => setTarget(null), TARGET_HIGHLIGHT_MS);
        return () => clearTimeout(timer);
    }, [target]);

    useEffect(() => {
        if (tab !== 'transcript' || searchFocusRequest === handledSearchFocus.current) return;
        handledSearchFocus.current = searchFocusRequest;
        searchRef.current?.focus();
        searchRef.current?.select();
    }, [searchFocusRequest, tab]);

    const {canSeek, seek, skip, toggle} = playback;
    const jumpTo = useCallback((citation: Citation, via: 'citation' | 'topic') => {
        const index = findSegmentIndexById(segments, citation.segmentId);
        if (canSeek) seek(citation.startTime, {play: true});
        selectTab('transcript', via);
        if (index >= 0) setTarget({index, nonce: Date.now()});
        logPlaybackSeek(via);
    }, [canSeek, seek, segments, selectTab]);

    const jumpToCitation = useCallback((citation: Citation) => jumpTo(citation, 'citation'), [jumpTo]);

    const searchStems = search.searchStems;
    const openTopic = useCallback((topic: InsightTopic) => {
        const cited = topic.citations?.find((citation) => findSegmentIndexById(segments, citation.segmentId) >= 0);
        if (cited) {
            jumpTo(cited, 'topic');
            return;
        }
        searchStems(topic.label);
        selectTab('transcript', 'topic');
    }, [jumpTo, searchStems, segments, selectTab]);

    const actions = useMemo<InsightsActions>(() => ({jumpToCitation, openTopic}), [jumpToCitation, openTopic]);

    const shortcutHandlers = useMemo<DrawerShortcutHandlers>(() => ({
        toggle,
        skip: (delta) => {
            if (!canSeek) return;
            skip(delta);
            logPlaybackSeek('keyboard');
        },
        focusSearch: () => {
            if (!hasTranscript) return;
            selectTab('transcript', 'keyboard');
            setSearchFocusRequest((count) => count + 1);
        },
        nextMatch: search.next,
        previousMatch: search.previous,
        selectTab: (next) => selectTab(next, 'keyboard'),
        toggleHelp: () => setHelpOpen((open) => !open),
    }), [canSeek, hasTranscript, search.next, search.previous, selectTab, skip, toggle]);
    const onShortcut = useDrawerShortcuts(shortcutHandlers, !contactRequest);

    const onEscape = (event: KeyboardEvent) => {
        if (contactRequest) {
            event.preventDefault();
            setContactRequest(null);
            return;
        }
        if (helpOpen) {
            event.preventDefault();
            setHelpOpen(false);
            return;
        }
        if (search.query) {
            event.preventDefault();
            search.clear();
        }
    };

    const generate = async (mode: GenerationMode) => {
        logGenerationRequested({callId, mode});
        try {
            if (mode === 'transcript') await requestTranscript({callId}).unwrap();
            else await requestSummary({callId}).unwrap();
            logGenerationSucceeded({callId, mode});
        } catch (error) {
            logGenerationFailed({callId, mode, status: errorStatus(error)});
        }
    };

    const openContact = (request: ContactPanelRequest) => {
        setContactRequest(request);
        logContactSuggestionOpened(request.mode);
    };

    const onContactSaved = () => {
        dispatch(invalidateCallInsights(callId));
    };

    const restoreFocus = (event: Event) => {
        if (!returnFocus?.isConnected) return;
        event.preventDefault();
        returnFocus.focus();
        requestAnimationFrame(() => {
            if (returnFocus.isConnected && document.activeElement !== returnFocus) returnFocus.focus();
        });
    };

    const exportTranscript = () => {
        downloadText(transcriptFileName(callId, call?.startedAt), transcriptToText(segments));
        logTranscriptExported();
    };

    const share = async () => {
        const time = playback.canSeek ? playback.currentTime : 0;
        try {
            await copyText(insightsShareUrl(callId, time));
            dispatch(showToast({
                title: intl.formatMessage(m.shareCopied),
                description: intl.formatMessage(m.shareCopiedDescription, {time: formatTime(time)}),
                type: 'success',
            }));
            logContentCopied('link');
        } catch {
            dispatch(showToast({description: intl.formatMessage(m.copyFailed), type: 'error'}));
        }
    };

    const blocker = generationBlocker(call, hasTranscript);
    const blockerText = (value: InsightsBlocker): string | null => {
        if (value === 'too_long') return intl.formatMessage(m.tooLong);
        if (value === 'not_recorded') return intl.formatMessage(m.notRecorded);
        if (value === 'deleted') return intl.formatMessage(m.summaryExpired);
        return null;
    };
    const availability: SummaryAvailability = {canGenerate: blocker === null, reason: blockerText(blocker)};

    const copyLabel = tab === 'summary' ? m.copySummary : tab === 'transcript' ? m.copyTranscript : m.copyConversation;
    const copyKind = tab === 'summary' ? 'summary' : tab === 'transcript' ? 'transcript' : 'ask';
    const copySource = (): string => {
        if (tab === 'summary') {
            return summaryToText(summary, {
                keyPoints: intl.formatMessage(m.keyPoints),
                nextSteps: intl.formatMessage(m.nextSteps),
                topics: intl.formatMessage(m.topics),
                meetings: intl.formatMessage(m.meetings),
            });
        }
        if (tab === 'transcript') return transcriptToText(segments);
        return (questionsQuery.data ?? []).map((item) => `Q: ${item.question}\nA: ${item.answer}`).join('\n\n');
    };
    const canCopy = tab === 'summary' ? Boolean(summary) : tab === 'transcript' ? hasTranscript : Boolean(questionsQuery.data?.length);

    const tabs: SegmentedOption<InsightsTab>[] = [
        {id: 'summary', label: intl.formatMessage(m.tabSummary)},
        {id: 'transcript', label: intl.formatMessage(m.tabTranscript)},
        ...(askEnabled ? [{id: 'ask' as const, label: intl.formatMessage(m.tabAsk)}] : []),
    ];

    const lanes = hasTranscript ? <SpeakerTimeline segments={segments} cast={cast} duration={playback.duration}/> : undefined;

    const renderPlayer = () => {
        if (deleted) return <RecordingDeletedNotice deletedAt={call?.recording?.deletedAt}/>;
        if (!audioAvailable) {
            return <p className="m-0 rounded-control bg-surface-muted px-3 py-3 text-footnote text-ink-muted">{intl.formatMessage(m.noRecording)}</p>;
        }
        if (!recordingUrl) {
            return (
                <div className="flex h-[52px] items-center">
                    {recordingFailed
                        ? <span role="alert" className="text-footnote text-danger-ink">{intl.formatMessage(m.recordingFailed)}</span>
                        : <PhoneHQSpinner size={18} label={intl.formatMessage(m.recordingLoading)}/>}
                </div>
            );
        }
        return (
            <div className="py-1" data-testid="insights-player">
                <InsightsPlayer speech={segments} downloadName={`call-${callId}.mp3`} lanes={lanes}/>
            </div>
        );
    };

    const renderTranscriptEmpty = () => {
        if (transcriptQuery.isLoading) return <SkeletonList label={intl.formatMessage(m.loading)} rows={6} rowHeight={56}/>;
        const canTranscribe = audioAvailable && blocker === null;
        return (
            <Centered>
                <p className="m-0 text-callout text-ink-muted">{intl.formatMessage(m.transcriptEmpty)}</p>
                {transcriptRequest.isLoading ? (
                    <GenerationStatus generation={{pending: true, error: null}} pendingLabel={intl.formatMessage(m.transcriptGenerating)}/>
                ) : canTranscribe && !isFinalError(transcriptRequest.error) ? (
                    <button type="button" onClick={() => void generate('transcript')} className={tintButton}>
                        <Sparkles size={14} aria-hidden="true" className={lucide}/>
                        {intl.formatMessage(transcriptRequest.error ? m.tryAgain : m.transcriptGenerate)}
                    </button>
                ) : (
                    blockerText(blocker) && <p className="m-0 text-footnote text-ink-muted">{blockerText(blocker)}</p>
                )}
                {transcriptRequest.error && !transcriptRequest.isLoading ? (
                    <p role="alert" className="m-0 text-footnote text-danger-ink">{generationErrorMessage(intl, transcriptRequest.error, m.transcriptFailed)}</p>
                ) : null}
            </Centered>
        );
    };

    const renderBody = () => {
        if (callLoading) return <Centered><PhoneHQSpinner size={24} label={intl.formatMessage(m.loading)}/></Centered>;
        if (callFailed || !call) {
            return <Centered><p role="alert" className="m-0 text-callout text-ink-muted">{intl.formatMessage(m.callLoadFailed)}</p></Centered>;
        }
        return (
            <>
                <div className="flex shrink-0 flex-col gap-3 px-6 pb-3 pt-2">
                    <InsightsParticipants call={call} cast={cast}/>
                    {renderPlayer()}
                    {hasTranscript && (!recordingUrl || deleted) && lanes}
                </div>
                <div className="flex shrink-0 items-center gap-1 px-6 pb-3">
                    <Segmented
                        options={tabs}
                        value={tab}
                        onChange={(next) => selectTab(next, 'click')}
                        label={intl.formatMessage(m.tabsLabel)}
                    />
                    <span className="flex-1"/>
                    <CopyButton
                        key={tab}
                        label={intl.formatMessage(copyLabel)}
                        getText={copySource}
                        disabled={!canCopy}
                        onCopied={() => logContentCopied(copyKind)}
                    />
                    <button
                        type="button"
                        onClick={() => void share()}
                        aria-label={intl.formatMessage(m.share)}
                        title={intl.formatMessage(m.share)}
                        className={iconButton}
                    >
                        <Share2 size={18} aria-hidden="true" className={lucide}/>
                    </button>
                </div>
                <div
                    role="tabpanel"
                    aria-label={tabs.find((option) => option.id === tab)?.label}
                    data-testid={`insights-panel-${tab}`}
                    className="flex min-h-0 flex-1 flex-col border-0 border-t border-solid border-line"
                >
                    {tab === 'summary' && (
                        <div className="min-h-0 flex-1 overflow-y-auto px-6 pb-24 pt-4">
                            <SummaryView
                                call={call}
                                summary={summary}
                                loading={summaryQuery.isLoading}
                                availability={availability}
                                generation={{pending: summaryRequest.isLoading, error: summaryRequest.error}}
                                onGenerate={(mode) => void generate(mode)}
                                onOpenContact={openContact}
                                followUp={followUp}
                            />
                        </div>
                    )}
                    {tab === 'transcript' && (hasTranscript ? (
                        <div className="flex min-h-0 flex-1 flex-col pt-3">
                            <div className="shrink-0 px-6 pb-2">
                                <TalkStatsBar stats={stats} cast={cast}/>
                            </div>
                            <TranscriptView ref={searchRef} segments={segments} search={search} target={target} onExport={exportTranscript}/>
                        </div>
                    ) : renderTranscriptEmpty())}
                    {tab === 'ask' && askEnabled && (
                        <div className="flex min-h-0 flex-1 flex-col pt-3">
                            <AskAiView callId={callId} enabled={hasTranscript}/>
                        </div>
                    )}
                </div>
            </>
        );
    };

    return (
        <Dialog.Root open onOpenChange={(open) => !open && close()}>
            <Dialog.Portal>
                <Dialog.Overlay className="fixed inset-0 z-40 bg-scrim motion-safe:animate-scrim-in"/>
                <Dialog.Content
                    ref={contentRef}
                    aria-describedby={undefined}
                    data-testid="call-insights-drawer"
                    onOpenAutoFocus={(event) => {
                        event.preventDefault();
                        contentRef.current?.focus();
                    }}
                    onEscapeKeyDown={onEscape}
                    onCloseAutoFocus={restoreFocus}
                    onKeyDown={onShortcut}
                    className="fixed inset-y-0 right-0 z-40 flex w-[min(600px,100%)] flex-col overflow-hidden rounded-l-sheet bg-surface text-ink shadow-overlay outline-none motion-safe:animate-insights-drawer-in"
                >
                    <PlaybackContext.Provider value={playback}>
                        <InsightsActionsContext.Provider value={actions}>
                            <div className="flex h-14 shrink-0 items-center gap-2 pl-6 pr-4">
                                <Sparkles size={18} aria-hidden="true" className={`${lucide} text-brand-ink`}/>
                                <Dialog.Title className="m-0 flex-1 truncate text-body-strong text-ink">{intl.formatMessage(m.title)}</Dialog.Title>
                                <button
                                    type="button"
                                    aria-label={intl.formatMessage(m.shortcutsButton)}
                                    aria-expanded={helpOpen}
                                    title={intl.formatMessage(m.shortcutsButton)}
                                    onClick={() => setHelpOpen((open) => !open)}
                                    className={iconButton}
                                >
                                    <Keyboard size={18} aria-hidden="true" className={lucide}/>
                                </button>
                                <button type="button" aria-label={intl.formatMessage(m.close)} onClick={close} className={iconButton}>
                                    <X size={18} aria-hidden="true" className={lucide}/>
                                </button>
                            </div>
                            {renderBody()}
                            {helpOpen && <ShortcutsHelp askEnabled={askEnabled} onClose={() => setHelpOpen(false)}/>}
                            {recordingUrl && <audio ref={playback.attachAudio} src={recordingUrl} preload="metadata" className="hidden"/>}
                            {contactRequest && call && (
                                <ContactPanel
                                    open
                                    onClose={() => setContactRequest(null)}
                                    contactId={contactRequest.mode === 'complete' ? contactRequest.contactId : undefined}
                                    initialPhoneNumber={contactRequest.mode === 'create' && call.clientMsisdn ? String(call.clientMsisdn) : undefined}
                                    prefill={contactRequest.prefill}
                                    onSaved={onContactSaved}
                                />
                            )}
                        </InsightsActionsContext.Provider>
                    </PlaybackContext.Provider>
                </Dialog.Content>
            </Dialog.Portal>
        </Dialog.Root>
    );
};
